import { ChangeDetectionStrategy, Component, Input, computed, signal } from "@angular/core";
import { User } from "../../../interfaces/users/user";

type AvatarRole = "physician" | "nurse" | "midwife" | "engineer" | "admin" | "other";

const PHYSICIAN_CODES = new Set([
	"158965000", // Physician
	"405277009", // Resident physician
	"62247001", // General practitioner
	"17561000", // Cardiologist
	"158970007", // Anaesthetist
	"159033005", // Dietitian
	"61894003", // Endocrinologist
	"71838004", // Gastroenterologist
	"56397003", // Neurologist
	"82296001", // Paediatrician
	"28411006", // Neonatologist
	"90655003", // Geriatrician
	"80584001", // Psychiatrist
	"309361002", // Child psychiatrist
	"18803008", // Dermatologist
	"309367003", // Gynaecologist-obstetrician
]);

const NURSE_CODES = new Set([
	"106292003", // Professional nurse
	"224535009", // Registered nurse
]);

const MIDWIFE_CODES = new Set([
	"158992006", // Midwife
	"309453006", // Registered midwife
]);

const PHARMACIST_CODES = new Set([
	"46255001", // Pharmacist
]);

const ENGINEER_CODES = new Set([
	"106269003", // Engineer
]);

function snomedToRole(snomedCt: string | undefined): AvatarRole {
	if (!snomedCt) return "other";
	const code = snomedCt.trim();
	if (PHYSICIAN_CODES.has(code) || PHARMACIST_CODES.has(code)) return "physician";
	if (NURSE_CODES.has(code)) return "nurse";
	if (MIDWIFE_CODES.has(code)) return "midwife";
	if (ENGINEER_CODES.has(code)) return "engineer";
	return "other";
}

@Component({
	selector: "app-avatar",
	imports: [],
	templateUrl: "./avatar.component.html",
	styleUrl: "./avatar.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
	private _user = signal<User | null>(null);

	@Input() set user(value: User | null) {
		this._user.set(value ?? null);
	}

	@Input() showCaption = true;
	@Input() size: "sm" | "md" = "md";

	protected sizeStyles = computed(() =>
		this.size === "sm"
			? { circlePx: 32, fontPx: 12, badgePx: 16, badgeIconPx: 10, borderPx: 1 }
			: { circlePx: 64, fontPx: 20, badgePx: 24, badgeIconPx: 14, borderPx: 2 }
	);

	protected firstName = computed(() => this._user()?.firstname ?? "");
	protected lastName = computed(() => this._user()?.lastname ?? "");
	protected role = computed<AvatarRole>(() => snomedToRole(this._user()?.job?.snomed_ct));

	protected jobLabel = computed(() => this._user()?.job?.label ?? "");

	protected initials = computed(() => {
		const first = this.firstName().trim().charAt(0).toUpperCase();
		const last = this.lastName().trim().charAt(0).toUpperCase();
		return first + last || "?";
	});

	protected avatarColor = computed(() => {
		const text = `${this.firstName()}-${this.lastName()}-${this.role()}`.toLowerCase();
		let hash = 0;
		for (let i = 0; i < text.length; i++) {
			hash = text.charCodeAt(i) + ((hash << 5) - hash);
		}
		const hue = Math.abs(hash) % 360;
		return `hsl(${hue}, 70%, 55%)`;
	});
}
